import type { FC } from "react"
import React from "react"

import type { Rectangle2d } from "@logic/lib/2d"
import { studioToScreen } from "@logic/lib/design"
import { scalePanelToImage } from "@logic/lib/utils"
import { useRecoilValue, selector } from "recoil"

import { siteLocalState } from "~/containers/Proposal/state/site/site-state"

import { panelGapState, selectedModuleState } from "../../state/elements/panels"
import {
  makePanelOriginMatrix,
  readonlyTransactors,
} from "../../state/elements/transactions"
import { preferredImageState } from "../../state/imageElements"
import { dragBoxState } from "../../state/inputs"
import { toolState } from "../../state/tool"
import { zoomState } from "../../state/view"

const panelMatrixPreviewState = selector<Rectangle2d[]>({
  key: `panelMatrixPreview`,
  get: ({ get }) => {
    const dragBox = get(dragBoxState)
    const tool = get(toolState)
    if (
      !(
        dragBox &&
        [`PanelCreatorPortrait`, `PanelCreatorLandscape`].includes(tool)
      )
    ) {
      return []
    }
    const gap = get(panelGapState)
    const { latitude } = get(siteLocalState)
    const { zoomLevel } = get(preferredImageState)
    const solarModule = get(selectedModuleState)
    const panelScale = scalePanelToImage({
      solarModule: { ...solarModule },
      latitude,
      zoomLevel,
    })
    const { h, w } =
      tool === `PanelCreatorPortrait`
        ? panelScale
        : { h: panelScale.w, w: panelScale.h }
    const zoom = get(zoomState)
    const offset = studioToScreen.point(
      {
        x: w + gap,
        y: h + gap,
      },
      zoom,
    )
    const orientation =
      tool === `PanelCreatorPortrait` ? `portrait` : `landscape`
    const points = makePanelOriginMatrix(readonlyTransactors(get), {
      orientation,
      isPreview: true,
    })
    const scale = studioToScreen.scale(
      {
        h,
        w,
      },
      zoom,
    )
    const panels: Rectangle2d[] = points.map(({ x, y }) => ({
      x,
      y,
      ...scale,
      r: 0,
    }))

    return panels
  },
})

const PreviewPanelMatrix_Internal: FC = () => {
  const previewPanels = useRecoilValue(panelMatrixPreviewState)

  return (
    <>
      {previewPanels.map(({ x, y, h, w }, i) => (
        <rect
          key={`previewPanel` + i}
          x={x}
          y={y}
          width={w}
          height={h}
          fill="#000"
        />
      ))}
    </>
  )
}

export const PreviewPanelMatrix = React.memo(PreviewPanelMatrix_Internal)
